import { faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, Group, Stack, Text, ThemeIcon } from '../../../core';
import { getArtifactFileMeta } from '../../artifact-file-meta';
import { useDownloadFeedback } from '../../use-download-feedback';

export interface FileArtifactCardProps {
  readonly filename: string;
  readonly downloadUrl: string;
  readonly sizeLabel?: string;
  readonly onDownload?: () => void | Promise<void>;
}

function buildMetaLabel(
  sizeLabel: string | undefined,
  typeName: string
): string {
  return [sizeLabel, typeName].filter(Boolean).join(' · ');
}

/** Standalone download card for a single generated file.
 *
 *  Superseded by `ArtifactGroup` + `ArtifactRow` for new chat emissions, but
 *  retained indefinitely: persisted conversations replay a2ui envelopes naming
 *  this component, and `AsyncTaskProgress` renders it directly for MooTask
 *  delivery artifacts. */
export function FileArtifactCard({
  filename,
  downloadUrl,
  sizeLabel,
  onDownload,
}: FileArtifactCardProps) {
  const { isDownloading, startDownload } = useDownloadFeedback(
    downloadUrl,
    onDownload
  );
  const fileType = getArtifactFileMeta(filename);
  const metaLabel = buildMetaLabel(sizeLabel, fileType.typeName);

  return (
    <Card withBorder radius="md" p="sm" style={{ width: '100%' }}>
      <Group wrap="nowrap" align="center" gap="xs">
        <ThemeIcon variant="light" size="md" radius="md">
          <FontAwesomeIcon icon={fileType.icon} />
        </ThemeIcon>

        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            {fileType.category}
          </Text>

          <Text size="sm" fw={700} truncate>
            {filename}
          </Text>

          {metaLabel && (
            <Text size="xs" c="dimmed">
              {metaLabel}
            </Text>
          )}
        </Stack>
      </Group>

      <Button
        mt="sm"
        size="xs"
        fullWidth
        loading={isDownloading}
        onClick={startDownload}
        leftSection={<FontAwesomeIcon icon={faArrowDown} />}
      >
        Download
      </Button>
    </Card>
  );
}
